import React, { useEffect, useState } from 'react';
import { 
    Card, 
    Table, 
    Statistic, 
    Row, 
    Col, 
    DatePicker, 
    Button, 
    Space, 
    Alert, 
    Tag, 
    Typography,
    Spin,
    message
} from 'antd';
import { ReloadOutlined, CalendarOutlined, PrinterOutlined } from '@ant-design/icons';
import { api } from '../api/services';
import { 
    ICustomerAccountingEntry, 
    ICustomerAccountBalance, 
    ICustomerAccountSummary,
    getTransactionSourceLabel
} from '../interfaces/accounting';
import { formatCurrency } from '../utils/formatters';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface CustomerAccountingTabProps {
    customerId: number;
}

export const CustomerAccountingTab: React.FC<CustomerAccountingTabProps> = ({ customerId }) => {
    const [accountingSummary, setAccountingSummary] = useState<ICustomerAccountSummary | null>(null);
    const [accountingEntries, setAccountingEntries] = useState<ICustomerAccountingEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().subtract(30, 'day'),
        dayjs()
    ]);

    const loadAccountingSummary = async () => {
        try {
            setLoading(true);
            const summary = await api.getCustomerAccountSummary(customerId);
            setAccountingSummary(summary);
        } catch (error) {
            console.error('Error loading customer accounting summary:', error);
            message.error('Failed to load accounting summary');
        } finally {
            setLoading(false);
        }
    };

    const loadAccountingEntries = async (fromDate?: string, toDate?: string) => {
        try {
            setEntriesLoading(true);
            const entries = await api.getCustomerAccountingEntries(customerId, fromDate, toDate);
            setAccountingEntries(entries);
        } catch (error) {
            console.error('Error loading customer accounting entries:', error);
            message.error('Failed to load accounting entries');
        } finally {
            setEntriesLoading(false);
        }
    };

    const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        setDateRange(dates || [null, null]);
    };

    const handleRefresh = () => {
        loadAccountingSummary();
        const fromDate = dateRange[0]?.format('YYYY-MM-DD');
        const toDate = dateRange[1]?.format('YYYY-MM-DD');
        loadAccountingEntries(fromDate, toDate);
    };

    const handleFilterEntries = () => {
        const fromDate = dateRange[0]?.format('YYYY-MM-DD');
        const toDate = dateRange[1]?.format('YYYY-MM-DD');
        loadAccountingEntries(fromDate, toDate);
    };

    const handlePrintStatement = async () => {
        try {
            const fromDate = dateRange[0]?.format('YYYY-MM-DD');
            const toDate = dateRange[1]?.format('YYYY-MM-DD');
            await api.downloadCustomerStatement(customerId, fromDate, toDate);
            message.success('Statement downloaded successfully');
        } catch (error) {
            console.error('Error downloading statement:', error);
            message.error('Failed to download statement');
        }
    };

    useEffect(() => {
        loadAccountingSummary();
        const fromDate = dateRange[0]?.format('YYYY-MM-DD');
        const toDate = dateRange[1]?.format('YYYY-MM-DD');
        loadAccountingEntries(fromDate, toDate);
    }, [customerId]);

    const entriesColumns = [
        {
            title: 'Date',
            dataIndex: 'transactionDate',
            key: 'transactionDate',
            render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
            sorter: (a: ICustomerAccountingEntry, b: ICustomerAccountingEntry) => 
                dayjs(a.transactionDate).unix() - dayjs(b.transactionDate).unix(),
            sortDirections: ['descend' as const, 'ascend' as const],
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Reference',
            dataIndex: 'reference',
            key: 'reference',
            render: (ref: string) => ref || '-',
        },
        {
            title: 'Debit',
            dataIndex: 'debitAmount',
            key: 'debitAmount',
            render: (amount: number) => amount > 0 ? formatCurrency(amount) : '-',
            align: 'right' as const,
            sorter: (a: ICustomerAccountingEntry, b: ICustomerAccountingEntry) => 
                a.debitAmount - b.debitAmount,
            sortDirections: ['descend' as const, 'ascend' as const],
        },
        {
            title: 'Credit',
            dataIndex: 'creditAmount',
            key: 'creditAmount',
            render: (amount: number) => amount > 0 ? formatCurrency(amount) : '-',
            align: 'right' as const,
            sorter: (a: ICustomerAccountingEntry, b: ICustomerAccountingEntry) => 
                a.creditAmount - b.creditAmount,
            sortDirections: ['descend' as const, 'ascend' as const],
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            render: (source: number) => (
                <Tag color="blue">{getTransactionSourceLabel(source)}</Tag>
            ),
        },
        {
            title: 'Journal #',
            dataIndex: 'journalNumber',
            key: 'journalNumber',
            render: (ref: string) => ref || '-',
        },
        {
            title: 'Status',
            dataIndex: 'journalStatus',
            key: 'journalStatus',
            render: (status: string) => {
                const color = status === 'Posted' ? 'green' : status === 'Draft' ? 'orange' : 'red';
                return <Tag color={color}>{status || 'Unknown'}</Tag>;
            },
        }
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div>
            {/* Account Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Current Balance"
                            value={accountingSummary?.account?.currentBalance || 0}
                            formatter={(value) => formatCurrency(Number(value))}
                            valueStyle={{ 
                                color: (accountingSummary?.account?.currentBalance || 0) >= 0 ? '#3f8600' : '#cf1322' 
                            }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Recent Transactions"
                            value={accountingSummary?.recentActivity?.transactionCount || 0}
                            suffix="transactions"
                        />
                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                            {accountingSummary?.recentActivity?.period}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Debits"
                            value={accountingSummary?.recentActivity?.totalDebits || 0}
                            formatter={(value) => formatCurrency(Number(value))}
                        />
                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                            {accountingSummary?.recentActivity?.period}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Net Change"
                            value={accountingSummary?.recentActivity?.netChange || 0}
                            formatter={(value) => formatCurrency(Number(value))}
                            valueStyle={{ 
                                color: (accountingSummary?.recentActivity?.netChange || 0) >= 0 ? '#3f8600' : '#cf1322' 
                            }}
                        />
                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                            {accountingSummary?.recentActivity?.period}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Account Details */}
            <Card style={{ marginBottom: 24 }}>
                <Row>
                    <Col span={12}>
                        <Title level={5}>Account Information</Title>
                        <p><strong>Account Code:</strong> {accountingSummary?.account?.code}</p>
                        <p><strong>Account Name:</strong> {accountingSummary?.account?.name}</p>
                        <p><strong>Account Status:</strong> 
                            <Tag color={accountingSummary?.account?.exists ? 'green' : 'orange'} style={{ marginLeft: 8 }}>
                                {accountingSummary?.account?.exists ? 'Active' : 'Not Created'}
                            </Tag>
                        </p>
                    </Col>
                </Row>
            </Card>

            {/* Account not exists warning */}
            {!accountingSummary?.account?.exists && (
                <Alert
                    message="No Accounting Activity"
                    description="This customer doesn't have any accounting transactions yet. The account will be created automatically when the first transaction occurs."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            {/* Transaction History */}
            <Card
                title="Transaction History"
                extra={
                    <Space>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                        />
                        <Button onClick={handleFilterEntries} icon={<CalendarOutlined />}>
                            Filter
                        </Button>
                        <Button onClick={handlePrintStatement} icon={<PrinterOutlined />} type="primary">
                            Print Statement
                        </Button>
                        <Button onClick={handleRefresh} icon={<ReloadOutlined />}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Table
                    dataSource={accountingEntries}
                    columns={entriesColumns}
                    rowKey="id"
                    loading={entriesLoading}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} of ${total} transactions`,
                    }}
                    scroll={{ x: 1200 }}
                />
            </Card>
        </div>
    );
};